import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, RefreshCw, Target, ChevronDown, ChevronUp } from "lucide-react";

/**
 * AI-powered summariser for a player's coach-notes journal.
 *
 * Fetches the cached summary on mount (GET /api/students/:id/coach-notes/summary)
 * and lets the coach regenerate (POST same route) once fresh notes accumulate.
 * The cache is stored on the backend so the button is idempotent across
 * sessions and doesn't burn LLM credits on every panel re-render.
 *
 * Renders as a compact card in both places the user requested:
 *  - inside CoachNotesTimeline (top of the notes list)
 *  - inside CoachAssessmentPanel (as decision-support before the rating rows)
 *
 * `currentNotesCount` (optional) enables the "N new notes since summary" hint
 * — pass it from the parent that already knows the notes count so we avoid
 * a duplicate GET on /coach-notes.
 */
export default function CoachNotesSummary({ studentId, currentNotesCount, variant = "full", hideWhenEmpty = false }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [ownNotesCount, setOwnNotesCount] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/students/${studentId}/coach-notes/summary`);
      setSummary(data.cached ? data : null);
    } catch (e) {
      // 403 for coaches who can't view this player is expected; stay quiet.
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  // When the parent doesn't pass a count, fetch it here — needed both for the
  // stale badge and (when hideWhenEmpty) to decide whether to render at all.
  const loadOwnCount = useCallback(async () => {
    if (typeof currentNotesCount === "number") return;
    try {
      const { data } = await api.get(`/students/${studentId}/coach-notes`);
      setOwnNotesCount(Array.isArray(data) ? data.length : 0);
    } catch {
      setOwnNotesCount(0);
    }
  }, [studentId, currentNotesCount]);

  useEffect(() => {
    if (!studentId) return;
    load();
    loadOwnCount();
    // Cross-component sync: refetch when any sibling adds/deletes a note or
    // regenerates the summary (see CoachNotesTimeline + generate() below).
    const onChanged = (e) => {
      if (!e?.detail || e.detail.studentId === studentId) {
        load();
        loadOwnCount();
      }
    };
    window.addEventListener("coach-notes:changed", onChanged);
    return () => window.removeEventListener("coach-notes:changed", onChanged);
  }, [studentId, load, loadOwnCount]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/students/${studentId}/coach-notes/summary`, null, { timeout: 120000 });
      setSummary(data);
      toast.success(summary ? "Summary refreshed" : "Summary generated");
      setExpanded(true);
      // Nudge sibling instances (e.g. the timeline card sitting on the same page).
      window.dispatchEvent(new CustomEvent("coach-notes:changed", { detail: { studentId } }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI summary failed");
    } finally {
      setGenerating(false);
    }
  };

  const effectiveCount = typeof currentNotesCount === "number" ? currentNotesCount : ownNotesCount;

  if (hideWhenEmpty && !summary && effectiveCount === 0) return null;

  if (loading) {
    return (
      <div className="border border-[#262626] bg-[#0A0A0A] p-3 text-[10px] uppercase tracking-wider text-[#525252]" data-testid="coach-notes-summary-loading">
        Loading AI summary…
      </div>
    );
  }

  const hasSummary = !!summary;
  const staleCount = hasSummary && typeof effectiveCount === "number"
    ? Math.max(0, effectiveCount - (summary.notes_count || 0))
    : 0;
  const isStale = staleCount > 0;
  const generatedAt = summary?.generated_at ? new Date(summary.generated_at) : null;
  const compact = variant === "compact";

  return (
    <div
      className={`border p-3 sm:p-4 ${hasSummary ? "border-[#CCFF00]/40 bg-[#CCFF00]/[0.03]" : "border-dashed border-[#262626] bg-[#0A0A0A]"}`}
      data-testid="coach-notes-summary"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-4 h-4" style={{ color: "#CCFF00" }} />
          <div className="label-mono" style={{ color: "#CCFF00" }}>
            AI COACH SUMMARY
          </div>
          {hasSummary && (
            <span className="text-[10px] text-[#525252] uppercase tracking-wider">
              · from {summary.notes_count} note{summary.notes_count === 1 ? "" : "s"}
              {generatedAt && ` · ${generatedAt.toLocaleDateString()}`}
            </span>
          )}
          {isStale && (
            <span
              className="label-mono px-1.5 py-0.5 text-[9px]"
              style={{ background: "#FF4500", color: "#FFFFFF" }}
              data-testid="coach-notes-summary-stale"
            >
              {staleCount} NEW
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasSummary && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-[10px] uppercase tracking-wider text-[#A3A3A3] hover:text-white inline-flex items-center gap-1"
              data-testid="coach-notes-summary-toggle"
            >
              {expanded ? <><ChevronUp className="w-3 h-3" /> Hide</> : <><ChevronDown className="w-3 h-3" /> Show</>}
            </button>
          )}
          <button
            type="button"
            onClick={generate}
            disabled={generating}
            className="btn-volt px-3 py-1.5 text-[10px] inline-flex items-center gap-1.5 disabled:opacity-40"
            data-testid={hasSummary ? "coach-notes-summary-regenerate" : "coach-notes-summary-generate"}
          >
            <RefreshCw className={`w-3 h-3 ${generating ? "animate-spin" : ""}`} />
            {generating ? "Thinking…" : hasSummary ? "Regenerate" : "Generate summary"}
          </button>
        </div>
      </div>

      {!hasSummary && !compact && (
        <div className="text-xs text-[#A3A3A3] mt-2 leading-relaxed">
          Distil recurring themes across every historic note into a compact bullet-list plus a next-session focus.
          Powered by Claude Sonnet 4.5 — one click, ~$0.
        </div>
      )}

      {hasSummary && expanded && (
        <div className="mt-3 space-y-3">
          {(summary.themes || []).length > 0 && (
            <div>
              <div className="label-mono mb-1.5 text-[10px]" style={{ color: "#A3A3A3" }}>
                RECURRING THEMES
              </div>
              <ul className="space-y-1.5 text-sm text-[#E5E5E5]" data-testid="coach-notes-summary-themes">
                {(summary.themes || []).map((t, i) => (
                  <li key={i} className="flex items-start gap-2 leading-relaxed">
                    <span className="mt-1.5 w-1.5 h-1.5 bg-[#CCFF00] shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {summary.next_focus && (
            <div className="border-l-2 border-[#CCFF00] pl-3">
              <div className="label-mono mb-1 text-[10px] inline-flex items-center gap-1" style={{ color: "#CCFF00" }}>
                <Target className="w-3 h-3" /> NEXT SESSION FOCUS
              </div>
              <div className="text-sm text-[#E5E5E5] leading-relaxed" data-testid="coach-notes-summary-next-focus">
                {summary.next_focus}
              </div>
            </div>
          )}
          {summary.error && (
            <div className="text-[10px] text-[#FF4500] uppercase tracking-wider">
              Last run had an error — try regenerating.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
